import {useEffect, useState} from 'react';

import Select from 'react-select';

import { getSlugsFromHash, setSlugsInHash } from './hash';

import type { OptionsType } from 'react-select';
import type { ReactElement } from 'react';
import type { Series } from './series';

interface PickerProps {
  onChange: (series: Series[]) => void
}

type SelectedCategory = {
  label: string
  value: string
};

type Category = {
  display: string
  slug: string
};

const defaultValues = getSlugsFromHash() || ['All-items', 'Housing', 'Medical-care'];

const seriesCache = new Map<string, Series>();
const slugToLabel = new Map<string, string>();

async function loadCategories(): Promise<Category[]> {
  const resp = await fetch('https://www.in2013dollars.com/api/categories');
  const categories = await resp.json();
  categories.forEach((category: Category) => {
    slugToLabel.set(category.slug, category.display);
  });
  return categories;
}

async function loadCategory(name: string): Promise<Series | undefined> {
  if (!seriesCache.has(name)) {
    const resp = await fetch(`https://www.in2013dollars.com/api/category/${name}`);
    const series = await resp.json();
    seriesCache.set(name, series);
  }
  return seriesCache.get(name);
}

function Picker({onChange}: PickerProps): ReactElement {
  const [categories, setCategories] = useState<Category[] | []>([]);
  const [values, setValues] = useState<SelectedCategory[]>([]);

  const doUpdate = async (vals: SelectedCategory[]) => {
    const seriesPromises = vals.map((val) => {
      return loadCategory(val.value);
    });
    const series = await Promise.all(seriesPromises);

    const ret = series.filter((serie): serie is Series => !!serie);

    // Update rest of app
    onChange(ret);
  };

  useEffect(() => {
    (async function load() {
      setCategories(await loadCategories());
      const hydratedValues = defaultValues.map(value => ({
        label: slugToLabel.get(value) || value,
        value: value,
      }));
      doUpdate(hydratedValues);
      setValues(hydratedValues);
    })();
  // eslint-disable-next-line
  }, []);

  const options = categories.map((category: Category) => (
    {
      value: category.slug,
      label: category.display,
    }
  ));

  const onSelect = async (vals: OptionsType<SelectedCategory>) => {
    // Update input
    const categories = vals.map(x => ({label: x.label, value: x.value}));
    setValues(categories);

    if (vals.length < 1) {
      onChange([]);
      return;
    }

    setSlugsInHash(categories.map(x => x.value));
    doUpdate(categories);
  };

  return (
    <Select isMulti value={values} options={options} onChange={onSelect} />
  );
}

export default Picker;
